import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useCurrency } from '../../context/CurrencyContext';
import { writeFetch } from '../../api';

const {
  FiX, FiHeart, FiGift, FiMessageSquare, FiCheck,
  FiChevronRight, FiChevronLeft, FiRepeat,
  FiShield, FiAlertCircle,
} = FiIcons;

const GIFT_OPTIONS = [
  { id: 'candle',  label: 'Candle',   emoji: '🕯️', desc: 'Light a candle in their memory' },
  { id: 'flowers', label: 'Flowers',  emoji: '🌸', desc: 'Send flowers to honor their life' },
  { id: 'tree',    label: 'Tree',     emoji: '🌳', desc: 'Plant a tree as a living tribute' },
  { id: 'dove',    label: 'Dove',     emoji: '🕊️', desc: 'Release a dove of peace' },
  { id: 'star',    label: 'Star',     emoji: '⭐', desc: 'Name a star in their honor' },
  { id: 'heart',   label: 'Heart',    emoji: '❤️', desc: 'A gift from the heart' },
];

const AMOUNTS = [25, 50, 100, 250, 500];

const NCB_DATA_URL    = 'https://app.nocodebackend.com/api/data';
const NCB_INSTANCE    = '53179_memorial_sanctuary';
const NCB_SECRET      = '8538d058b9882f511ddf0d39c7d91bc6027abeeb4b8b69523ca8a6d1fdef';
const DONATIONS_TABLE = 'memorial_donations_1776800000';
const CAMPAIGNS_TABLE = 'donation_campaigns_1776500000';

function ncbHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${NCB_SECRET}`,
    'X-Database-Instance': NCB_INSTANCE,
  };
}

function ncbUrl(path) {
  const sep = path.includes('?') ? '&' : '?';
  return `${NCB_DATA_URL}/${path}${sep}Instance=${NCB_INSTANCE}`;
}

/* FIX #4: Robust direct-NCB donation submission — bypasses Express server entirely */
async function recordDonationDirectly(donationData) {
  const transactionRef = `TXN-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

  const payload = {
    owner_id:           'guest',
    memorial_id:        donationData.memorial_id    || '',
    memorial_name:      donationData.memorial_name  || '',
    donor_name:         donationData.is_anonymous ? 'Anonymous' : (donationData.donor_name || 'Anonymous'),
    donor_email:        donationData.donor_email    || '',
    amount:             Number(donationData.amount) || 0,
    donor_tip:          Number(donationData.donor_tip) || 0,
    platform_fee_pct:   Number(donationData.platform_fee_pct) || 10,
    gift_type:          donationData.gift_type      || '',
    gift_option:        donationData.gift_option    || '',
    message:            donationData.message        || '',
    donation_type:      donationData.donation_type  || 'one_time',
    recurring_interval: donationData.donation_type === 'recurring' ? (donationData.recurring_interval || 'monthly') : '',
    is_anonymous:       donationData.is_anonymous   ? 1 : 0,
    charity_name:       donationData.charity_name   || 'Memorial Fund',
    campaign_id:        donationData.campaign_id    || '',
    transaction_ref:    transactionRef,
    status:             'completed',
  };

  const res  = await fetch(ncbUrl(`create/${DONATIONS_TABLE}`), {
    method: 'POST',
    headers: ncbHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json();

  if (!res.ok) {
    const msg = data?.message || data?.error || JSON.stringify(data);
    throw new Error(`NCB error: ${msg}`);
  }

  /* Update campaign wallet balance */
  if (donationData.campaign_id) {
    try {
      const campRes  = await fetch(ncbUrl(`read/${CAMPAIGNS_TABLE}/${donationData.campaign_id}`), { headers: ncbHeaders() });
      const campData = await campRes.json();
      if (campRes.ok && campData.data) {
        const newRaised = (Number(campData.data.raised_amount) || 0) + Number(donationData.amount);
        const newCount  = (Number(campData.data.donor_count)   || 0) + 1;
        await fetch(ncbUrl(`update/${CAMPAIGNS_TABLE}/${donationData.campaign_id}`), {
          method: 'PUT',
          headers: ncbHeaders(),
          body: JSON.stringify({ raised_amount: newRaised, donor_count: newCount }),
        });
      }
    } catch { /* silent — wallet update is best-effort */ }
  }

  return { success: true, donationId: data.id || data.data?.id, transactionRef, data: payload };
}

/* FIX #2: Modal now accepts tipOptions + allowTip as props */
const DonateInMemoryModal = ({
  isOpen, onClose, memorial, onSuccess,
  campaign, feeRate = 0.10,
  tipOptions = [1, 5, 10],
  allowTip   = true,
}) => {
  const { formatCurrency } = useCurrency();
  const [step, setStep] = useState(1);

  const [selectedGift,      setSelectedGift]      = useState(null);
  const [amount,            setAmount]            = useState(50);
  const [customAmount,      setCustomAmount]      = useState('');
  const [donationType,      setDonationType]      = useState('one_time');
  const [recurringInterval, setRecurringInterval] = useState('monthly');
  const [donorName,         setDonorName]         = useState('');
  const [donorEmail,        setDonorEmail]        = useState('');
  const [message,           setMessage]           = useState('');
  const [isAnonymous,       setIsAnonymous]       = useState(false);
  const [tipPct,            setTipPct]            = useState(0);
  const [submitting,        setSubmitting]        = useState(false);
  const [error,             setError]             = useState('');

  const memorialName = memorial?.name || memorial?.title || 'the memorial';
  const finalAmount  = customAmount ? parseFloat(customAmount) : amount;
  const tipAmount    = finalAmount * (tipPct / 100);
  const totalCharge  = finalAmount + tipAmount;
  const platformFee  = finalAmount * feeRate;
  const netToCharity = finalAmount - platformFee;

  /* FIX #4: Use direct NCB submission — no Express server dependency */
  const handleSubmit = useCallback(async () => {
    setError('');
    if (!isAnonymous && !donorName.trim()) { setError('Please enter your name or donate anonymously.'); return; }
    if (isNaN(finalAmount) || finalAmount < 1) { setError('Please enter a valid donation amount (minimum $1).'); return; }

    setSubmitting(true);
    try {
      const result = await recordDonationDirectly({
        memorial_id:        memorial?.id || '',
        memorial_name:      memorialName,
        campaign_id:        campaign?.id || '',
        donor_name:         isAnonymous ? 'Anonymous' : donorName.trim(),
        donor_email:        donorEmail.trim(),
        amount:             finalAmount,
        donor_tip:          tipAmount,
        platform_fee_pct:   feeRate * 100,
        gift_type:          selectedGift?.id    || '',
        gift_option:        selectedGift?.label || '',
        message:            message.trim(),
        donation_type:      donationType,
        recurring_interval: recurringInterval,
        is_anonymous:       isAnonymous,
        charity_name:       campaign?.beneficiary_name || memorial?.donations?.charity || 'Memorial Fund',
      });
      onSuccess?.({ donorName: isAnonymous ? 'Anonymous' : donorName, amount: finalAmount, result });
    } catch (err) {
      console.error('Donation error:', err);
      setError(err.message || 'Failed to record donation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [donorName, donorEmail, message, isAnonymous, finalAmount, tipAmount, selectedGift, donationType, recurringInterval, memorial, campaign, feeRate, onSuccess, memorialName]);

  if (!isOpen) return null;

  return createPortal(
    /* FIX #2: Proper viewport-constrained modal with scrollable inner content */
    <div className="fixed inset-0 z-[9999] bg-accent-navy/70 backdrop-blur-md flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-[32px] shadow-2xl w-full max-w-lg border border-brand-dark flex flex-col"
        style={{ maxHeight: 'calc(100vh - 2rem)' }}>

        {/* Fixed Header */}
        <div className="bg-gradient-to-r from-accent-navy to-[#1a2a5e] p-6 flex items-center justify-between rounded-t-[32px] flex-shrink-0">
          <div>
            <h2 className="font-serif text-xl text-white leading-none mb-1">Give to this Legacy</h2>
            <p className="text-[10px] font-black uppercase tracking-widest text-white/50">In memory of {memorialName.split(' ')[0]}</p>
            {campaign && <p className="text-[10px] text-accent-gold/80 mt-0.5 font-medium truncate max-w-[240px]">{campaign.title}</p>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60 hover:text-white flex-shrink-0 ml-3">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Step tabs */}
        <div className="flex border-b border-brand-light flex-shrink-0">
          {['Gift & Amount', 'Your Details', 'Confirm'].map((label, i) => (
            <div key={i} className={`flex-1 py-2.5 text-center text-[9px] font-black uppercase tracking-widest transition-all ${
              step === i + 1 ? 'text-accent-navy border-b-2 border-accent-gold' : 'text-text-muted'
            }`}>{label}</div>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <AnimatePresence mode="wait">

              {/* ── Step 1 ── */}
              {step === 1 && (
                <motion.div key="s1" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-5">
                  {/* Gift type */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">Select a Gift Type</p>
                    <div className="grid grid-cols-3 gap-2">
                      {GIFT_OPTIONS.map(g => (
                        <button key={g.id} onClick={() => setSelectedGift(selectedGift?.id === g.id ? null : g)}
                          className={`flex flex-col items-center gap-1 p-3 rounded-2xl border-2 transition-all ${
                            selectedGift?.id === g.id ? 'border-accent-navy bg-accent-navy/5' : 'border-brand-dark hover:border-accent-gold/50'
                          }`}>
                          <span className="text-xl leading-none">{g.emoji}</span>
                          <span className={`text-[9px] font-black uppercase tracking-widest ${selectedGift?.id === g.id ? 'text-accent-navy' : 'text-text-muted'}`}>{g.label}</span>
                        </button>
                      ))}
                    </div>
                    {selectedGift && <p className="text-[10px] text-text-muted mt-1.5 text-center italic">{selectedGift.desc}</p>}
                  </div>

                  {/* Amount */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">Donation Amount</p>
                    <div className="grid grid-cols-5 gap-1.5 mb-2">
                      {AMOUNTS.map(a => (
                        <button key={a} onClick={() => { setAmount(a); setCustomAmount(''); }}
                          className={`py-2 rounded-xl border-2 text-sm font-black transition-all ${
                            amount === a && !customAmount ? 'bg-accent-navy text-white border-accent-navy' : 'border-brand-dark text-text-muted hover:border-accent-navy/50'
                          }`}>${a}</button>
                      ))}
                    </div>
                    <input type="number" value={customAmount} min="1"
                      onChange={e => { setCustomAmount(e.target.value); setAmount(0); }}
                      placeholder="Custom amount ($)"
                      className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-medium" />
                  </div>

                  {/* Frequency */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">Frequency</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[{ id: 'one_time', label: 'One-time', icon: FiGift }, { id: 'recurring', label: 'Recurring', icon: FiRepeat }].map(opt => (
                        <button key={opt.id} onClick={() => setDonationType(opt.id)}
                          className={`flex items-center gap-2 p-3 rounded-2xl border-2 text-[11px] font-black uppercase tracking-widest transition-all ${
                            donationType === opt.id ? 'border-accent-navy bg-accent-navy/5 text-accent-navy' : 'border-brand-dark text-text-muted hover:border-accent-gold/50'
                          }`}>
                          <SafeIcon icon={opt.icon} className="w-3.5 h-3.5" /> {opt.label}
                        </button>
                      ))}
                    </div>
                    {donationType === 'recurring' && (
                      <select value={recurringInterval} onChange={e => setRecurringInterval(e.target.value)}
                        className="mt-2 w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-medium bg-white">
                        {['weekly', 'monthly', 'quarterly', 'annually'].map(v => (
                          <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* FIX #3: Optional tip — 1%, 5%, 10% from config */}
                  {allowTip && (
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-1.5">
                        Optional Tip for Platform <span className="font-normal normal-case tracking-normal text-text-muted">(helps us keep the lights on)</span>
                      </p>
                      <div className="flex gap-2">
                        <button onClick={() => setTipPct(0)}
                          className={`flex-1 py-2 rounded-xl border-2 text-[10px] font-black transition-all ${tipPct === 0 ? 'bg-accent-navy text-white border-accent-navy' : 'border-brand-dark text-text-muted hover:border-accent-navy/50'}`}>
                          None
                        </button>
                        {tipOptions.map(t => (
                          <button key={t} onClick={() => setTipPct(t)}
                            className={`flex-1 py-2 rounded-xl border-2 text-[10px] font-black transition-all ${tipPct === t ? 'bg-accent-gold text-white border-accent-gold' : 'border-brand-dark text-text-muted hover:border-accent-gold/50'}`}>
                            {t}%
                          </button>
                        ))}
                      </div>
                      {tipPct > 0 && (
                        <p className="text-[10px] text-text-muted mt-1.5 text-center">
                          Tip: {formatCurrency(tipAmount)} · Total charge: {formatCurrency(totalCharge)}
                        </p>
                      )}
                    </div>
                  )}
                </motion.div>
              )}

              {/* ── Step 2 ── */}
              {step === 2 && (
                <motion.div key="s2" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-1.5">Your Name</label>
                    <input type="text" value={donorName} onChange={e => setDonorName(e.target.value)}
                      disabled={isAnonymous} placeholder="Full name…"
                      className="w-full px-4 py-3.5 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-medium disabled:opacity-40" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-1.5">Email <span className="font-normal normal-case">(optional)</span></label>
                    <input type="email" value={donorEmail} onChange={e => setDonorEmail(e.target.value)} placeholder="your@email.com"
                      className="w-full px-4 py-3.5 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-medium" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-1.5">Personal Message <span className="font-normal normal-case">(optional)</span></label>
                    <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3}
                      placeholder="Share a memory or a word of comfort…"
                      className="w-full px-4 py-3.5 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-medium resize-none" />
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div onClick={() => setIsAnonymous(!isAnonymous)}
                      className={`w-10 h-5 rounded-full relative transition-colors flex-shrink-0 cursor-pointer ${isAnonymous ? 'bg-accent-gold' : 'bg-brand-dark'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${isAnonymous ? 'right-0.5' : 'left-0.5'}`} />
                    </div>
                    <span className="text-[11px] font-bold text-text-muted">Donate anonymously</span>
                  </label>
                </motion.div>
              )}

              {/* ── Step 3 ── */}
              {step === 3 && (
                <motion.div key="s3" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-4">
                  <div className="bg-brand-light/50 border border-brand-dark rounded-3xl divide-y divide-brand-dark overflow-hidden">
                    {[
                      ['Donor',        isAnonymous ? 'Anonymous' : (donorName || 'Not specified')],
                      ['Gift',         selectedGift ? `${selectedGift.emoji} ${selectedGift.label}` : 'No gift selected'],
                      ['Donation',     formatCurrency(finalAmount)],
                      ['Frequency',    donationType === 'recurring' ? `Recurring (${recurringInterval})` : 'One-time'],
                      ...(tipPct > 0 ? [['Platform Tip', `${tipPct}% · ${formatCurrency(tipAmount)}`]] : []),
                      ['Total Charge', formatCurrency(totalCharge)],
                      ['Net to Cause', formatCurrency(netToCharity)],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between items-center px-5 py-2.5">
                        <span className="text-[10px] font-black uppercase tracking-widest text-text-muted">{k}</span>
                        <span className="text-sm font-bold text-accent-navy">{v}</span>
                      </div>
                    ))}
                  </div>
                  {message && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                      <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-1">Your Message</p>
                      <p className="text-sm text-amber-800 italic">"{message}"</p>
                    </div>
                  )}
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-2xl">
                    <SafeIcon icon={FiShield} className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <p className="text-[11px] text-green-700 font-medium">Your donation is processed securely. 100% of the net amount goes to the designated beneficiary.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {error && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="mt-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0" /> {error}
              </motion.div>
            )}
          </div>
        </div>

        {/* Fixed Footer Navigation */}
        <div className="flex gap-3 p-6 pt-4 border-t border-brand-light flex-shrink-0 bg-white rounded-b-[32px]">
          {step > 1 && (
            <button onClick={() => { setError(''); setStep(s => s - 1); }}
              className="px-5 py-3.5 rounded-2xl border-2 border-brand-dark text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold transition-all flex items-center gap-1.5">
              <SafeIcon icon={FiChevronLeft} className="w-3.5 h-3.5" /> Back
            </button>
          )}
          {step < 3 ? (
            <button onClick={() => { setError(''); setStep(s => s + 1); }}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg shadow-accent-navy/20">
              Continue <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-accent-gold text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-all shadow-lg shadow-accent-gold/30 disabled:opacity-50">
              {submitting
                ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <><SafeIcon icon={FiHeart} className="w-4 h-4" /> Confirm Donation</>
              }
            </button>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default DonateInMemoryModal;